import type { CustomerAlternativeContext } from '../../../../../services/customerRequests/types';
import { ClipboardPaste } from "lucide-react";
import CopyIngredientsButton from "./CopyIngredientsButton";
import React, { useEffect, useRef, useState } from "react";
import { greenColorClass } from "../../../../../styling/stylingHelper";
import { cn } from "../../../../../lib/utils";
import { eur } from "../../../../orderhistory/utils";
import { useTranslation } from "react-i18next";
import { useCostPriceDetail } from "./useCostPriceDetail";
import { PracticeScopeProvider, isPracticeScopeRoot, scopedAnchor } from "../../../../onboarding/practice/practiceScope";
import AddIngredients from "./AddIngredients";
import AlternativesView from "./AlternativesView";
import CategoryAssignmentModal from "../../../../costPrice/CategoryAssignmentModal";
import EditRevenueLinkModal from "../../modals/EditRevenueLinkModal";
import EditMenuPriceModal from "../../modals/EditMenuPriceModal";
import { DeleteConfirmationModal } from "../../../../common/DeleteConfirmationModal";
import DetailHeader from "./DetailHeader";
import SummaryCards from "./SummaryCards";
import BadgesRow from "./BadgesRow";
import SubrecipesSection from "./SubrecipesSection";
import NestedMenuItemsSection from "./NestedMenuItemsSection";
import IngredientsSection from "./IngredientsSection";
import LinkCustomIngredientModal from "./LinkCustomIngredientModal";
import { productKey } from "../../../../../utils/reports/shared";
import SubrecipeDetailView from "./SubrecipeDetailView";
import ByoChoicesSection from "./ByoChoicesSection";
import { CostTargets } from "../../../../../utils/costTargets";
import { COST_PRICE_TYPE_CHOICE } from "../../../../../utils/costPriceUtils";
import { CostPriceDuplicateNameApiError } from "../../../../../services/costPrice/costPriceService";

export interface CostPriceDetailBodyProps {
    costPriceId: string;
    customerAlternative?: CustomerAlternativeContext;
    onClose: () => void;
    costTargets?: CostTargets;
    // Restaurant-wide default ingredient waste %, the inherit fallback shown as
    // the per-ingredient placeholder and folded into the live cost.
    defaultWastePercentage?: number;
    // BYO = build-your-own. Caller handles lazy find-or-create of the CHOICE
    // analysis for `choiceName` (inheriting the parent dish's `parentCategoryId`)
    // and then re-opens the editor with that analysis's id. Optional — when
    // omitted, choice rows in the BYO hub are non-interactive (admin views).
    onEditChoice?: (choiceName: string, parentCategoryId: string | undefined) => void;
    // When set, the header swaps the close icon for a back arrow that triggers
    // this callback. Used when the editor is a child view (CHOICE opened from a
    // BYO dish, or a step in the staged-setup flow): the user wants to return to
    // the parent, not close the panel.
    onBack?: () => void;
    // BYO dish context: id of the parent BYO dish this CHOICE was opened from.
    // When set, quantity edits in the editor are stored as per-dish overrides
    // on that dish (`byoChoiceOverrides`) instead of editing the shared CHOICE.
    byoParentId?: string;
    // Chrome selection. "slideOver" (the CostPriceDetailModal wrapper) renders the
    // fixed backdrop + sliding panel with the maximize/close animation; "embedded"
    // (default) renders the editor inline for hosts like the staged-setup flow that
    // supply their own frame. All editor behavior is identical across both.
    host?: "slideOver" | "embedded";
    // Optional strip rendered full-width directly below the DetailHeader (above the
    // scroll area / compact bar) in both hosts — the setup flow passes its stepper
    // here so it sits under the header row. Only shows on the main editor view.
    subHeader?: React.ReactNode;
    // On mount, scroll to the order-history ingredient row whose resolver key
    // (productKey) matches this and focus its quantity input. No-op when absent or
    // no matching row is found. Used by the setup flow to land the user on the
    // just-added ingredient.
    initialFocusProductKey?: string;
}

// Module-level cache of each analysis's inner scroll position. The detail editor
// fully remounts when hopping between a BYO dish and one of its CHOICEs (keyed on
// id in the parent), which would otherwise reset the scroll to the top. We stash
// the dish's scroll offset on the way into a choice and restore it on the way back
// so the user lands on the choice row they just edited, not the top of the list.
const detailScrollPositions = new Map<string, number>();

/**
 * Compact KPI strip pinned to the top of the scroll area when the full
 * SummaryCards have scrolled out of view. Three figures (menu price, total
 * cost, cost %) so the user can keep an eye on the numbers while editing a
 * long ingredient list.
 */
const CompactSummaryBar: React.FC<{
    menuPrice: number;
    totalCost: number;
    costPercentage: number | null;
    isCostBad: boolean;
}> = ({ menuPrice, totalCost, costPercentage, isCostBad }) => {
    const { t } = useTranslation();
    return (
        <div className="flex items-center justify-between gap-4 sm:gap-6">
            <div className="flex flex-col min-w-0">
                <span className="text-[11px] leading-3 text-[#475467] dark:text-textSecondary-dark uppercase tracking-wide">
                    {t('costPrice.detail.compact.menuPrice')}
                </span>
                <span className="mt-1 text-[15px] leading-5 font-semibold text-[#101828] dark:text-textPrimary-dark">
                    {eur(menuPrice)}
                </span>
            </div>
            <div className="flex flex-col min-w-0">
                <span className="text-[11px] leading-3 text-[#475467] dark:text-textSecondary-dark uppercase tracking-wide">
                    {t('costPrice.detail.compact.totalCost')}
                </span>
                <span className="mt-1 text-[15px] leading-5 font-semibold text-[#101828] dark:text-textPrimary-dark">
                    {eur(totalCost)}
                </span>
            </div>
            <div className="flex flex-col items-end min-w-0">
                <span className="text-[11px] leading-3 text-[#475467] dark:text-textSecondary-dark uppercase tracking-wide">
                    {t('costPrice.detail.compact.costPct')}
                </span>
                <span
                    className={cn(
                        "mt-1 inline-flex items-center rounded-md px-2 py-0.5 text-[14px] leading-5 font-semibold",
                        costPercentage === null
                            ? "bg-[#F2F4F7] text-[#475467] dark:bg-borderPrimary-dark dark:text-textSecondary-dark"
                            : isCostBad
                                ? "bg-[#FFF4E6] text-[#eb8d00]"
                                : "bg-[#E7FBF5] text-[#166534]",
                    )}
                >
                    {costPercentage !== null ? `${costPercentage.toFixed(1)}%` : '—'}
                </span>
            </div>
        </div>
    );
};

/**
 * Inline rename affordance for the cost price name. Non-editing = the title h2 +
 * a pencil; editing = a text input with confirm/cancel. `onSave` persists and
 * RE-THROWS on failure — a CostPriceDuplicateNameApiError keeps edit mode and
 * shows an inline error; empty/unchanged is a no-op that just closes the editor.
 */
const EditableTitle: React.FC<{
    name: string;
    marginClass: string;
    // CHOICE analyses are matched to BYO add-ons by name (costPriceUtils.ts) — a
    // rename would orphan the link, so the pencil is suppressed for them and the
    // plain title renders instead.
    editable: boolean;
    onSave: (name: string) => Promise<void>;
}> = ({ name, marginClass, editable, onSave }) => {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(name);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { if (!editing) setValue(name); }, [name, editing]);
    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

    const begin = () => { setValue(name); setError(null); setEditing(true); };
    const cancel = () => { setEditing(false); setError(null); setValue(name); };

    const commit = async () => {
        const trimmed = value.trim();
        if (!trimmed || trimmed === name) { cancel(); return; }
        setSaving(true);
        setError(null);
        try {
            await onSave(trimmed);
            setEditing(false);
        } catch (err) {
            setError(err instanceof CostPriceDuplicateNameApiError
                ? t('costPrice.rename.duplicate')
                : t('costPrice.rename.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    // CHOICE: render the plain title with the original h2 margins, no pencil.
    if (!editable) {
        return (
            <h2 className={`${marginClass} text-[28px] leading-10 tracking-[-0.28px] font-bold text-[#161924] dark:text-textPrimary-dark`}>
                {name}
            </h2>
        );
    }

    if (editing) {
        return (
            <div className={marginClass}>
                <div className="flex items-center gap-2">
                    <input
                        ref={inputRef}
                        type="text"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commit();
                            else if (e.key === 'Escape') cancel();
                        }}
                        disabled={saving}
                        className="flex-1 min-w-0 text-[24px] leading-8 tracking-[-0.24px] font-bold text-[#161924] dark:text-textPrimary-dark bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded-lg px-2 py-1 outline-none"
                    />
                    <button
                        type="button"
                        onClick={commit}
                        disabled={saving}
                        title={t('common.save')}
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark disabled:opacity-50"
                    >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.6668 5L7.50016 14.1667L3.3335 10" stroke="#16A34A" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={cancel}
                        disabled={saving}
                        title={t('common.cancel')}
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark disabled:opacity-50"
                    >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 5L5 15M5 5L15 15" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
                {error && <p className="mt-1 text-[13px] leading-5 text-red-600 dark:text-red-400">{error}</p>}
            </div>
        );
    }

    return (
        <div className={`${marginClass} flex items-center gap-2`}>
            <h2 className="min-w-0 truncate text-[28px] leading-10 tracking-[-0.28px] font-bold text-[#161924] dark:text-textPrimary-dark">
                {name}
            </h2>
            <button
                type="button"
                onClick={begin}
                title={t('costPrice.rename.title')}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg hover:bg-[#F2F4F7] dark:hover:bg-borderPrimary-dark transition-colors"
            >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.166 2.5C14.3849 2.28113 14.6447 2.1075 14.9307 1.98905C15.2167 1.8706 15.5232 1.80963 15.8327 1.80963C16.1422 1.80963 16.4487 1.8706 16.7347 1.98905C17.0206 2.1075 17.2805 2.28113 17.4993 2.5C17.7182 2.71887 17.8918 2.97871 18.0103 3.26468C18.1287 3.55064 18.1897 3.85714 18.1897 4.16667C18.1897 4.4762 18.1287 4.7827 18.0103 5.06866C17.8918 5.35463 17.7182 5.61447 17.4993 5.83333L6.24935 17.0833L1.66602 18.3333L2.91602 13.75L14.166 2.5Z" stroke="#475467" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>
    );
};

/**
 * The full cost-price editor experience — the main body plus the view-swap ladder
 * (AddIngredients / AlternativesView / SubrecipeDetailView). Self-loads via
 * useCostPriceDetail. Rendered inside the slide-over by CostPriceDetailModal
 * (`host="slideOver"`), or inline by other hosts (`host="embedded"`, default).
 */
const CostPriceDetailBody: React.FC<CostPriceDetailBodyProps> = ({
    costPriceId,
    onClose,
    costTargets,
    defaultWastePercentage = 0,
    onEditChoice,
    onBack,
    byoParentId,
    host = "embedded",
    subHeader,
    initialFocusProductKey,
    customerAlternative,
}) => {
    const { t } = useTranslation();
    const detail = useCostPriceDetail(costPriceId, onClose, costTargets, defaultWastePercentage, byoParentId);

    const proposalIngredientId = useRef(customerAlternative?.ingredientId);
    const [proposalCompleted, setProposalCompleted] = useState(false);
    const initializedProposal = useRef<string | null>(null);
    const proposalIngredient = detail.enrichedAnalysis?.ingredients.find(row => row.id === customerAlternative?.ingredientId);
    const openProposalAlternatives = detail.openAlternatives;
    useEffect(() => {
        if (!customerAlternative || !proposalIngredient || initializedProposal.current === customerAlternative.requestId) return;
        initializedProposal.current = customerAlternative.requestId;
        openProposalAlternatives(proposalIngredient);
    }, [customerAlternative, proposalIngredient, openProposalAlternatives]);

    // Guided tour: this drawer is the tour's own only when it holds the practice dish.
    // Every other instance on screen reads false and ignores the events below, which is
    // what stops a tour step from driving a real editor.
    const practiceScope = isPracticeScopeRoot(costPriceId);

    // Open / close the Add Ingredients view for a user who presses Next instead of clicking.
    useEffect(() => {
        if (!practiceScope) return;
        const onOpen = () => detail.setShowAddIngredients(true);
        const onClose = () => detail.setShowAddIngredients(false);
        window.addEventListener('onboarding:cost-open-add-ingredients', onOpen);
        window.addEventListener('onboarding:cost-close-add-ingredients', onClose);
        return () => {
            window.removeEventListener('onboarding:cost-open-add-ingredients', onOpen);
            window.removeEventListener('onboarding:cost-close-add-ingredients', onClose);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceScope]);

    // Sticky compact summary bar: appears when the full SummaryCards scrolls
    // out of view so the user can keep watching menu price / total cost / cost %
    // while editing a long ingredient list. IntersectionObserver roots on the
    // scroll container so we don't react to outer page scroll.
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const summaryCardsRef = useRef<HTMLDivElement>(null);
    const [showStickySummary, setShowStickySummary] = useState(false);

    useEffect(() => {
        const sentinel = summaryCardsRef.current;
        const root = scrollContainerRef.current;
        if (!sentinel || !root) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                // Show the compact bar ONLY once the full SummaryCards has scrolled
                // off the TOP of the scroll area. `!isIntersecting` alone is also
                // true when the summary sits BELOW the viewport at rest — which
                // happens in a short scroll container (the embedded ProductSetupModal
                // host) and wrongly pinned the bar open at scroll-top. Gate on the
                // sentinel being above the root's top edge to catch only the
                // scrolled-past-top case (behaviour-identical in the tall slide-over).
                const rootTop = entry.rootBounds?.top ?? 0;
                setShowStickySummary(!entry.isIntersecting && entry.boundingClientRect.top < rootTop);
            },
            { root, threshold: 0 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
        // Re-attach whenever the inner view swaps (main → AddIngredients → main).
    }, [detail.showAddIngredients, detail.selectedSubRecipeName, detail.enrichedAnalysis?.id]);

    // Restore the inner scroll position saved before navigating into a CHOICE
    // (see handleEditChoiceWithScroll). Runs once enrichment is loaded so the
    // list has its real height; the rAF loop waits for layout before scrolling.
    useEffect(() => {
        if (!detail.enrichedAnalysis?.id) return;
        const saved = detailScrollPositions.get(costPriceId);
        if (!saved) return;
        const root = scrollContainerRef.current;
        if (!root) return;
        const tryRestore = () => {
            if (root.scrollHeight >= saved + root.clientHeight) {
                root.scrollTop = saved;
                detailScrollPositions.delete(costPriceId);
            } else {
                requestAnimationFrame(tryRestore);
            }
        };
        requestAnimationFrame(tryRestore);
    }, [detail.enrichedAnalysis?.id, costPriceId]);

    // Stash this dish's scroll offset before opening a choice, so the back hop
    // (which remounts this editor) can land the user back on the same row.
    const handleEditChoiceWithScroll = (name: string) => {
        if (scrollContainerRef.current) {
            detailScrollPositions.set(costPriceId, scrollContainerRef.current.scrollTop);
        }
        onEditChoice?.(name, undefined);
    };

    if (!detail.enrichedAnalysis) return null;

    const analysis = detail.enrichedAnalysis;
    const copyButton = !practiceScope && (detail.hasCopyIngredients || detail.hasCopySubrecipes) ? (
        <CopyIngredientsButton
            hasIngredients={detail.hasCopyIngredients}
            hasSubrecipes={detail.hasCopySubrecipes}
            onCopy={detail.handleCopyIngredients}
        />
    ) : undefined;
    // BYO = build-your-own. `byo` is attached by enrichCostPriceWithCurrentPrices
    // when the dish's linked revenue items contain choices in the trailing 2-month window.
    const isByo = !!analysis.byo;
    // A CHOICE analysis (an add-on) uses the add-on category scope for its own
    // category control; everything else uses the revenue scope.
    const isChoice = (analysis as any).costPriceType === COST_PRICE_TYPE_CHOICE;
    const ownCategories = isChoice
        ? detail.addonCategories
        : detail.categoriesStore.categories.filter(c => (c.scope ?? 'revenue') === 'revenue');
    // When composer mode is on, the headline cards reflect the user's current
    // selection rather than the weighted average for the whole window.
    const summaryMenuPrice = detail.composerMode && detail.composed
        ? detail.composed.price
        : detail.menuPriceForCalculation;
    const summaryTotalCost = detail.composerMode && detail.composed
        ? detail.composed.cost
        : analysis.totalCost;
    const summaryCostPercentage = detail.composerMode && detail.composed
        ? detail.composed.costPct
        : detail.costPercentage;
    const summaryProfitMargin = detail.composerMode && detail.composed
        ? detail.composed.price - detail.composed.cost
        : detail.profitMargin;

    // Confirmation copy for the per-section "remove all" action.
    const clearSectionCopy = {
        ingredients: { title: t('costPrice.detail.clearIngredientsTitle'), message: t('costPrice.detail.clearIngredientsMessage') },
        nested: { title: t('costPrice.detail.clearNestedTitle'), message: t('costPrice.detail.clearNestedMessage') },
        subrecipes: { title: t('costPrice.detail.clearSubrecipesTitle'), message: t('costPrice.detail.clearSubrecipesMessage') },
    } as const;

    const titleMarginClass = `${detail.matrix || detail.category || analysis.menuItemName ? 'mt-3' : ''} ${detail.isChoiceWithDishContext ? 'mb-2' : 'mb-6'}`;

    // The editor content (view-swap ladder). Chrome (slide-over vs inline) wraps this.
    const content = detail.showAddIngredients ? (
        <AddIngredients
            analysisId={analysis.id}
            restaurantId={detail.restaurantId!}
            onBack={() => detail.setShowAddIngredients(false)}
            onClose={detail.handleClose}
            onIngredientAdded={detail.handleIngredientAdded}
            defaultWastePercentage={defaultWastePercentage}
        />
    ) : detail.alternativesIngredient ? (
        <AlternativesView
            primary={detail.alternativesIngredient}
            alternatives={detail.alternativesIngredient.alternatives ?? []}
            restaurantId={detail.restaurantId!}
            defaultWastePercentage={defaultWastePercentage}
            onChange={detail.handleSaveAlternatives}
            onSwap={detail.handleSwapAlternative}
            customerAlternative={!proposalCompleted && detail.alternativesIngredient.id === proposalIngredientId.current ? customerAlternative : undefined}
            onReviewDone={() => setProposalCompleted(true)}
            onReviewSaved={(ingredient, fresh) => { proposalIngredientId.current = ingredient.id; detail.openAlternatives(ingredient, fresh); }}
            propagationSourceId={analysis.id}
            requestContext={!practiceScope && !customerAlternative && !isChoice && !detail.alternativesIngredient.custom && !detail.alternativesIngredient.subrecipeId && !detail.alternativesIngredient.nestedCostPriceId && !detail.alternativesIngredient.sourceRestaurantId ? { type: 'propose_alternative', restaurantId: analysis.restaurantId, targetLabel: analysis.costPriceName, source: { kind: 'cost_price', id: analysis.id }, proposalContext: { analysisId: analysis.id, ingredientId: detail.alternativesIngredient.id } } : undefined}
            onBack={() => detail.setAlternativesIngredient(null)}
            onClose={detail.handleClose}
        />
    ) : detail.selectedSubRecipeName ? (
        <SubrecipeDetailView
            subrecipeId={detail.selectedSubRecipeName}
            onBack={() => detail.setSelectedSubRecipeName(null)}
            onClose={detail.handleClose}
        />
    ) : (
        <>
            <DetailHeader
                onToggleMaximize={() => detail.setIsMaximized(prev => !prev)}
                onDelete={() => detail.setShowDeleteModal(true)}
                onClose={detail.handleClose}
                onBack={onBack}
            />

            {/* Host-supplied strip (e.g. the setup flow's stepper), full-width
                directly below the header row and above the scroll area. */}
            {subHeader && (
                <div className="flex-shrink-0 border-b border-[#E8E8E9] dark:border-borderPrimary-dark">
                    {subHeader}
                </div>
            )}

            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 sm:px-5 pb-5 pt-5 md:pt-4 relative">
                {detail.revenueLinkRefreshFailed && (
                    <p role="status" className="mb-4 rounded-lg p-3 text-sm bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200">
                        {t('costPrice.revenueLink.refreshFailed')}
                    </p>
                )}
                {/* Sticky compact summary — pinned to scroll-container top,
                    fades in only when the full SummaryCards is out of view.
                    `-mx-/px-` extends edge-to-edge then re-applies the gutter. */}
                <div
                    className={cn(
                        "sticky -top-5 md:-top-4 z-20 -mx-4 sm:-mx-5 px-4 sm:px-5",
                        "bg-white/95 dark:bg-background-dark/95 backdrop-blur supports-[backdrop-filter]:bg-white/80",
                        "overflow-hidden transition-all duration-150",
                        // Collapse height/padding/border when hidden so the bar reserves
                        // no space at the top of the scroll area (only meant to
                        // appear once the full SummaryCards scrolls out of view).
                        showStickySummary
                            ? "max-h-40 py-3 opacity-100 border-b border-[#E8E8E9] dark:border-borderPrimary-dark"
                            : "max-h-0 py-0 opacity-0 pointer-events-none border-b border-transparent",
                    )}
                >
                    <CompactSummaryBar
                        menuPrice={summaryMenuPrice}
                        totalCost={summaryTotalCost}
                        costPercentage={summaryCostPercentage}
                        isCostBad={detail.isCostBad}
                    />
                </div>

                {/* Mobile header */}
                <div className="flex items-center gap-4 md:hidden mb-5">
                    <button type="button" onClick={onBack ?? detail.handleClose}>
                        <img src="/assets/arrow-narrow-left.svg" alt="arrow-narrow-left" />
                    </button>
                    <span className="text-[24px] leading-9 font-semibold tracking-[-0.24px] text-[#181624] dark:text-textPrimary-dark">
                        {t('costPrice.detail.mobileTitle')}
                    </span>
                </div>

                <BadgesRow
                    matrix={detail.matrix}
                    category={detail.category ?? null}
                    menuItemName={analysis.menuItemName}
                    costPriceType={(analysis as any).costPriceType}
                    showTypeSelector={detail.showTypeSelector}
                    onCategoryClick={detail.handleCategoryClick}
                    onRevenueLinkClick={() => detail.setShowEditRevenueLinkModal(true)}
                    onToggleTypeSelector={() => detail.setShowTypeSelector(!detail.showTypeSelector)}
                    onTypeChange={detail.handleTypeChange}
                    isAddOn={isChoice}
                />

                <div data-onboarding={scopedAnchor(practiceScope, "cost-detail-rename")}>
                    <EditableTitle
                        name={analysis.costPriceName}
                        marginClass={titleMarginClass}
                        editable={!isChoice}
                        onSave={detail.handleSaveCostPriceName}
                    />
                </div>

                {/* BYO dish context: quantity edits here are stored per dish,
                    structural edits (add/remove ingredient) stay shared. */}
                {detail.isChoiceWithDishContext && detail.byoDishContextName && (
                    <p className="mb-6 text-[13px] leading-5 text-[#475467] dark:text-textSecondary-dark">
                        {t('costPrice.byo.dishContextHint', { dish: detail.byoDishContextName })}
                    </p>
                )}

                <div ref={summaryCardsRef}>
                    <SummaryCards
                        profitMargin={summaryProfitMargin}
                        unitsSold={isByo ? analysis.byo!.baseQty : detail.unitsSold}
                        menuPrice={summaryMenuPrice}
                        totalCost={summaryTotalCost}
                        costPercentage={summaryCostPercentage}
                        isCostBad={detail.isCostBad}
                        onEditMenuPrice={() => detail.setShowEditMenuPriceModal(true)}
                    />
                </div>

                {/* BYO = build-your-own. Base section (subrecipes / nested /
                    ingredients) sits ABOVE the choices section so the user reads
                    "what's always in the dish" first, then "what the customer adds". */}
                {isByo && (
                    <div className="mt-6 mb-2">
                        <h3 className="text-[18px] leading-6 font-semibold text-[#101828] dark:text-textPrimary-dark">
                            {t('costPrice.byo.baseSectionTitle')}
                        </h3>
                        <p className="mt-1 text-[13px] text-[#475467] dark:text-textSecondary-dark">
                            {t('costPrice.byo.baseSectionHint')}
                        </p>
                    </div>
                )}

                <SubrecipesSection
                    headerActions={!detail.hasCopyIngredients ? copyButton : undefined}
                    subrecipes={detail.subrecipesWithTotals}
                    inlineQuantities={detail.inlineSubrecipeQuantities}
                    confirmingDeleteKey={detail.confirmingDeleteSubrecipeName}
                    onQuantityChange={(id, value) => detail.setInlineSubrecipeQuantities(prev => ({ ...prev, [id]: value }))}
                    onSaveEdit={detail.handleSaveEditSubrecipe}
                    onDelete={detail.handleDeleteSubrecipe}
                    onConfirmDelete={detail.handleConfirmDeleteSubrecipe}
                    onClearAll={detail.canAccessAutomation ? () => detail.handleClearSection('subrecipes') : undefined}
                    overriddenKeys={detail.isChoiceWithDishContext ? detail.overriddenSectionKeys : undefined}
                    onResetOverride={detail.isChoiceWithDishContext ? detail.handleResetChoiceOverride : undefined}
                />

                <NestedMenuItemsSection
                    menuItems={detail.nestedMenuItemsWithTotals}
                    hasSubrecipes={detail.subrecipesWithTotals.length > 0}
                    inlineQuantities={detail.inlineNestedMenuItemQuantities}
                    confirmingDeleteId={detail.confirmingDeleteNestedMenuItemId}
                    onQuantityChange={(id, value) => detail.setInlineNestedMenuItemQuantities(prev => ({ ...prev, [id]: value }))}
                    onSaveEdit={detail.handleSaveEditNestedMenuItem}
                    onDelete={detail.handleDeleteNestedMenuItem}
                    onConfirmDelete={detail.handleConfirmDeleteNestedMenuItem}
                    onClearAll={detail.canAccessAutomation ? () => detail.handleClearSection('nested') : undefined}
                    overriddenKeys={detail.isChoiceWithDishContext ? detail.overriddenSectionKeys : undefined}
                    onResetOverride={detail.isChoiceWithDishContext ? detail.handleResetChoiceOverride : undefined}
                />

                <IngredientsSection
                    headerActions={copyButton}
                    requestContext={!practiceScope ? { restaurantId: analysis.restaurantId, targetLabel: analysis.costPriceName, source: { kind: 'cost_price', id: analysis.id } } : undefined}
                    ingredients={detail.groupedIngredients['null']}
                    inlineQuantities={detail.inlineIngredientQuantities}
                    confirmingDeleteId={detail.confirmingDeleteIngredientId}
                    onQuantityChange={(id, value) => detail.setInlineIngredientQuantities(prev => ({ ...prev, [id]: value }))}
                    onSaveEdit={detail.handleSaveEditIngredient}
                    onDelete={detail.handleDeleteIngredient}
                    onConfirmDelete={detail.handleConfirmDeleteIngredient}
                    onClearAll={detail.canAccessAutomation ? () => detail.handleClearSection('ingredients') : undefined}
                    defaultWastePercentage={defaultWastePercentage}
                    inlineWaste={detail.inlineIngredientWaste}
                    onWasteChange={(id, value) => detail.setInlineIngredientWaste(prev => ({ ...prev, [id]: value }))}
                    onSaveWaste={detail.handleSaveEditIngredientWaste}
                    overriddenKeys={detail.isChoiceWithDishContext ? detail.overriddenSectionKeys : undefined}
                    onResetOverride={detail.isChoiceWithDishContext ? detail.handleResetChoiceOverride : undefined}
                    onOpenAlternatives={detail.openAlternatives}
                    onLinkCustomIngredient={detail.openCustomLink}
                    focusProductKey={initialFocusProductKey}
                />

                <button
                    type="button"
                    data-onboarding={scopedAnchor(practiceScope, "cost-add-ingredient")}
                    className={`mt-6 flex w-full items-center justify-center h-11 text-[16px] leading-6 font-semibold gap-2 ${greenColorClass} rounded-lg`}
                    onClick={() => detail.setShowAddIngredients(true)}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 4.16699V15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.1665 10H15.8332" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t('costPrice.detail.addIngredient')}
                </button>

                {!practiceScope && detail.canPasteIngredients && (
                    <button
                        type="button"
                        disabled={detail.isPasting}
                        title={t('costPrice.clipboard.source', { name: detail.clipboardSourceName })}
                        onClick={detail.handlePasteIngredients}
                        className="mt-2 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-borderPrimary dark:border-borderPrimary-dark text-sm font-semibold text-textSecondary dark:text-textSecondary-dark hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
                    >
                        <ClipboardPaste size={18} />
                        {t(detail.isPasting ? 'costPrice.clipboard.pasting' : 'costPrice.clipboard.paste')}
                    </button>
                )}
                {detail.clipboardFeedback && (
                    <p role="status" className="mt-2 text-center text-sm text-textSecondary dark:text-textSecondary-dark">
                        {detail.clipboardFeedback}
                    </p>
                )}

                {/* Opt-out for dishes that don't need cost setup (deposits, POS artifacts):
                    marking drops the dish from the setup-score denominators. Deliberately
                    subdued — adding ingredients stays the main path. */}
                {(analysis.ingredients.length === 0 || analysis.markedIrrelevant) && (
                    <button
                        type="button"
                        className="mt-2 w-full text-center text-sm text-gray-400 dark:text-textQuaternary-dark hover:text-gray-600 dark:hover:text-gray-300 py-1"
                        onClick={detail.handleToggleIrrelevant}
                    >
                        {analysis.markedIrrelevant
                            ? t('costPrice.detail.unmarkIrrelevant')
                            : t('costPrice.detail.markIrrelevant')}
                    </button>
                )}

                {/* Choices section sits BELOW the base — the user first defines
                    what's always in the dish, then sees the customer's add-ons.
                    "Try combinations" flips this section into composer mode
                    (toggle replaces the chevron, SummaryCards reflect the live
                    composed cost/price/%). Nothing persisted. */}
                {isByo && onEditChoice && (
                    <ByoChoicesSection
                        groups={detail.byoChoiceGroups}
                        // New CHOICE rows start unassigned (their own add-on category
                        // scope), so we don't inherit the parent dish's revenue category.
                        onEditChoice={handleEditChoiceWithScroll}
                        onAssignChoiceCategory={detail.handleChoiceCategoryClick}
                        weightedMode={detail.weightedMode}
                        composerSelection={detail.composerSelection}
                        onSetWeighted={detail.setWeighted}
                        onToggleChoice={detail.toggleComposerChoice}
                        unweightedCategoryKeys={detail.unweightedCategoryKeys}
                        onSetCategoryWeighted={detail.setCategoryWeighted}
                    />
                )}
            </div>
        </>
    );

    // All editor modals — rendered once regardless of the active view or chrome.
    const modals = (
        <>
            {detail.editingItemCategory && (
                <CategoryAssignmentModal
                    itemName={detail.editingItemCategory.itemName}
                    currentCategoryId={detail.editingItemCategory.currentCategoryId}
                    categories={ownCategories}
                    onSelectCategory={(categoryId) => detail.handleChangeItemCategory(detail.editingItemCategory!.itemName, categoryId)}
                    onCreateAndAssignCategory={detail.handleCreateAndAssignCategory}
                    onReorderCategories={detail.handleReorderCategories}
                    onCategoryUpdated={async () => { await detail.categoriesStore.reload(); }}
                    onClose={() => detail.setEditingItemCategory(null)}
                />
            )}

            {/* Add-on category assignment for a BYO choice (scope='addon'). */}
            {detail.editingChoiceCategory && (
                <CategoryAssignmentModal
                    itemName={detail.editingChoiceCategory.choiceName}
                    currentCategoryId={detail.editingChoiceCategory.currentCategoryId}
                    categories={detail.addonCategories}
                    onSelectCategory={(categoryId) => detail.handleChangeChoiceCategory(detail.editingChoiceCategory!.choiceName, categoryId)}
                    onCreateAndAssignCategory={detail.handleCreateAndAssignChoiceCategory}
                    onReorderCategories={detail.handleReorderCategories}
                    onCategoryUpdated={async () => { await detail.categoriesStore.reload(); }}
                    onClose={() => detail.setEditingChoiceCategory(null)}
                />
            )}

            {detail.showEditRevenueLinkModal && (
                <EditRevenueLinkModal
                    currentMenuItemName={analysis.menuItemName ?? null}
                    revenueItems={detail.revenueItems}
                    onSave={detail.handleSaveRevenueLink}
                    onClose={() => detail.setShowEditRevenueLinkModal(false)}
                />
            )}

            {detail.showEditMenuPriceModal && (
                <EditMenuPriceModal
                    analysis={analysis}
                    currentRevenuePrice={detail.currentRevenuePrice}
                    onSave={detail.handleSaveCustomMenuPrice}
                    onReset={detail.handleResetCustomMenuPrice}
                    onClose={() => detail.setShowEditMenuPriceModal(false)}
                />
            )}

            {detail.customLinkIngredient && (
                <LinkCustomIngredientModal
                    ingredient={detail.customLinkIngredient}
                    itemId={analysis.id}
                    // Order-history rows already on this dish, keyed by canonical product
                    // identity. Picking one of them is allowed (one product can serve two
                    // prep steps) but must not be silent.
                    existingRows={analysis.ingredients
                        .filter(row => row.id !== detail.customLinkIngredient!.id
                            && !row.custom && !row.subrecipeId && !row.nestedCostPriceId && !!row.productName)
                        .map(row => ({
                            key: productKey(row.platform ?? '', row.articleId, row.productName),
                            quantityNeeded: row.quantityNeeded,
                            stdUnit: row.quantityInfo?.stdUnit,
                        }))}
                    defaultWastePercentage={defaultWastePercentage}
                    onApply={detail.handleApplyCustomLink}
                    onClose={() => detail.setCustomLinkIngredient(null)}
                />
            )}

            <DeleteConfirmationModal
                isOpen={detail.showDeleteModal}
                title={t('costPrice.detail.deleteConfirmTitle')}
                message={t('costPrice.detail.deleteConfirmMessage', { name: analysis.costPriceName })}
                confirmLabel={t('costPrice.deleteAnalysis')}
                cancelLabel={t('common.cancel')}
                onConfirm={detail.handleDeleteCostPrice}
                onCancel={() => detail.setShowDeleteModal(false)}
            />

            <DeleteConfirmationModal
                isOpen={detail.clearSectionTarget !== null}
                title={detail.clearSectionTarget ? clearSectionCopy[detail.clearSectionTarget].title : ''}
                message={detail.clearSectionTarget ? clearSectionCopy[detail.clearSectionTarget].message : ''}
                confirmLabel={t('costPrice.detail.clearSectionConfirm')}
                cancelLabel={t('common.cancel')}
                onConfirm={detail.handleConfirmClearSection}
                onCancel={() => detail.setClearSectionTarget(null)}
            />
        </>
    );

    if (host === "slideOver") {
        return (
            <>
                <div
                    className="fixed inset-0 z-30 flex justify-end bg-[#111827]/40 dark:bg-[#111827]/40 pt-[72px] md:pt-0"
                    onClick={detail.handleClose}
                >
                    <div
                        data-onboarding={scopedAnchor(practiceScope, "cost-detail-panel")}
                        className={`h-full w-full bg-white dark:bg-background-dark shadow-xl flex flex-col transform transition-all duration-250 ease-out ${detail.isPanelVisible ? "translate-x-0" : "translate-x-full"
                            } ${detail.isMaximized
                                ? "max-w-full md:max-w-[1024px]"
                                : "max-w-full md:max-w-[584px]"
                            }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <PracticeScopeProvider value={practiceScope}>{content}</PracticeScopeProvider>
                    </div>
                </div>
                {modals}
            </>
        );
    }

    // Embedded chrome: plain full-height column the host frames itself.
    return (
        <div className="flex h-full w-full flex-col bg-white dark:bg-background-dark">
            <PracticeScopeProvider value={practiceScope}>{content}</PracticeScopeProvider>
            {modals}
        </div>
    );
};

export default CostPriceDetailBody;
